/**
 * Shared, honest, role-aware toast for a lifecycle transition that auto-stages a
 * lifecycle invoice (opening→active, closing→closed).
 *
 * ONE source of truth so every entry point — the bulk billing review modal, the
 * route board "Marcar Activo", the lifecycle-billing gate, the AI assistant —
 * tells the user the SAME true thing instead of three diverging toasts
 * ("created in Billing → Collect" / "is in Billing → Collect" / "Lifecycle updated.").
 *
 * Honesty: the autostage trigger runs SYNCHRONOUSLY inside set_customer_lifecycle_status,
 * so by the time the RPC resolves it has either created the draft OR logged a skip
 * row in lifecycle_invoice_stage_failures. We snapshot the latest skip BEFORE the
 * RPC and compare AFTER — a NEW row means staging skipped (and why); same row means
 * it created the invoice. FAIL-SAFE: if either query errors we never claim an
 * invoice — we nudge the user to verify (the original "lying toast" bug).
 *
 * Role awareness: only admin/billing can navigate to Drafts (FEATURE_ACCESS.invoices),
 * so only they get the "invoice in Billing → Collect" copy. supervisor/worker can transition but
 * not see Drafts → a generic "Pool activated/closed" (no misleading Drafts pointer,
 * and no stage-failures query they may lack RLS for).
 */

import { supabase } from '../../supabase';
import { withTimeout } from '../async/withTimeout';
import { formatCurrency } from '../format/format';
import { formatInvoiceNumber } from '../../services/invoiceService';

// iOS suspends the PWA mid-request; an un-timed-out stage-failures read can
// hang forever and freeze whatever awaits it (a modal's onConfirmed, a bulk
// apply). 8s is generous for a single-table indexed lookup — past it the
// query is effectively dead and the caller should treat it as unconfirmed,
// never silently hang.
const STAGE_FAILURE_QUERY_TIMEOUT_MS = 8000;

// Mirrors the reason labels used in lifecycle transition toasts / Activity.
const REASON_LABEL = {
  no_eligible_visits: { id: 'billing.lifecycleFailures.reason.noEligibleVisits', def: 'No eligible visits to bundle' },
  snooze_active: { id: 'billing.lifecycleFailures.reason.snoozeActive', def: 'Auto-billing snoozed' },
  duplicate_draft: { id: 'billing.lifecycleFailures.reason.duplicateDraft', def: 'An invoice already exists' },
  company_read_only: { id: 'billing.lifecycleFailures.reason.companyReadOnly', def: 'The company is read-only' },
  unknown_error: { id: 'billing.lifecycleFailures.reason.unknownError', def: 'Unknown error' },
};

/** Only these two canonical transitions auto-stage a lifecycle invoice. */
export function isAutoStagingTransition(fromStatus, toStatus) {
  return (fromStatus === 'opening' && toStatus === 'active')
    || (fromStatus === 'closing' && toStatus === 'closed');
}

/**
 * Clear, transition-specific success message for a NON-staging lifecycle change
 * (active→closing, closed→opening, …). Replaces the vague "Lifecycle updated."
 * the route handlers used to show. Staging transitions go through
 * reportLifecycleTransitionToast instead (they speak to the invoice outcome).
 */
export function nonStagingTransitionMessage({ fromStatus, toStatus, name, intl }) {
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  if (fromStatus === 'active' && toStatus === 'closing') {
    return t('feedback.lifecycle.closingStarted', 'Closing started for {name}.', { name });
  }
  if (fromStatus === 'closed' && toStatus === 'opening') {
    return t('feedback.lifecycle.reopened', '{name} reopened for the season.', { name });
  }
  return t('feedback.lifecycle.statusUpdated', "{name}'s status was updated.", { name });
}

/**
 * Most recent stage-failure row for a customer. Returns `{ row, errored }` so the
 * caller can FAIL SAFE — a query error (OR a timeout — see STAGE_FAILURE_QUERY_
 * TIMEOUT_MS above) must NOT be read as "invoice created".
 *
 * `tenantId` is OPTIONAL (RLS already scopes the row to the caller's tenant);
 * pass it when the caller already holds it so the query is doubly-scoped —
 * defense in depth, never the only gate. Omitting it keeps existing callers
 * working unchanged.
 */
export async function latestStageFailure(customerId, tenantId) {
  try {
    let query = supabase
      .from('lifecycle_invoice_stage_failures')
      .select('id, reason')
      .eq('customer_id', customerId);
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await withTimeout(
      Promise.resolve(query.order('created_at', { ascending: false }).limit(1)),
      STAGE_FAILURE_QUERY_TIMEOUT_MS,
    );
    if (error) return { row: null, errored: true };
    return { row: (data && data[0]) || null, errored: false };
  } catch {
    // Covers a real query error AND a TimeoutError alike — either way we
    // cannot confirm, so we never claim "created".
    return { row: null, errored: true };
  }
}

/**
 * Bulk twin of `latestStageFailure` — one query for N customers instead of N
 * queries. Returns a `Map<customerId, {row, errored}>` so per-customer
 * decisions can reuse `decideTransitionToast` unchanged.
 *
 * A query error OR a timeout (STAGE_FAILURE_QUERY_TIMEOUT_MS) fails every
 * entry to `errored:true` (never a silent "created" for the whole batch); a
 * customer with no failure row at all maps to `{row:null, errored:false}`
 * (same shape `latestStageFailure` returns).
 *
 * `tenantId` is OPTIONAL — see the same note on `latestStageFailure`.
 */
export async function latestStageFailuresForCustomers(customerIds, tenantId) {
  const ids = Array.from(new Set((customerIds || []).map(String)));
  if (ids.length === 0) return new Map();

  const map = new Map();
  for (const id of ids) map.set(id, { row: null, errored: false });

  try {
    let query = supabase
      .from('lifecycle_invoice_stage_failures')
      .select('id, customer_id, reason')
      .in('customer_id', ids);
    if (tenantId) query = query.eq('tenant_id', tenantId);
    const { data, error } = await withTimeout(
      Promise.resolve(query.order('created_at', { ascending: false })),
      STAGE_FAILURE_QUERY_TIMEOUT_MS,
    );

    if (error) {
      for (const id of ids) map.set(id, { row: null, errored: true });
      return map;
    }

    // Rows arrive newest-first ACROSS all customers, so the first time we see
    // a given customer_id in this loop is that customer's most recent row —
    // no per-customer grouping/sort needed.
    for (const row of data || []) {
      const cid = String(row.customer_id);
      if (map.has(cid) && map.get(cid).row === null) {
        map.set(cid, { row: { id: row.id, reason: row.reason }, errored: false });
      }
    }
    return map;
  } catch {
    for (const id of ids) map.set(id, { row: null, errored: true });
    return map;
  }
}

/**
 * Pure decision from before/after stage-failure snapshots. DOM/intl-free → testable.
 *   'created'     → staging produced the draft (success copy)
 *   'noInvoice'   → a NEW skip row appeared (info, with reason)
 *   'unconfirmed' → a query errored, can't tell (info, "verify" nudge)
 *
 * LOCKSTEP (read before touching `canBill` anywhere in this file): 'created'
 * is inferred from the ABSENCE of a new skip row, not from a positive read of
 * the invoice itself — so it is only trustworthy when the caller's role can
 * actually SEE `lifecycle_invoice_stage_failures` (RLS policy
 * `lifecycle_invoice_stage_failures_select_policy`, currently
 * admin/supervisor/billing). Every `canBill` gate in this file must stay a
 * SUBSET of that policy's roles — `canAccessFeature(role, 'invoices')` today
 * resolves to {admin, billing}, which is ⊆ {admin, supervisor, billing}. If
 * `canBill` were ever widened past the policy's roles, the query would 403/
 * RLS-filter to zero rows for the widened role and this function would read
 * that as "no skip row" → 'created' → a FALSE "invoice created" toast. Fails
 * open, not closed — this is the one part of the module that cannot self-
 * protect, so it is enforced by keeping the role sets in lockstep, not by code.
 *
 * @returns {{ kind: 'created'|'noInvoice'|'unconfirmed', reason?: string }}
 */
export function decideTransitionToast({ before, after }) {
  if (before?.errored || after?.errored) return { kind: 'unconfirmed' };
  if (after?.row && (!before?.row || after.row.id !== before.row.id)) {
    return { kind: 'noInvoice', reason: after.row.reason };
  }
  return { kind: 'created' };
}

/**
 * Bulk twin of `decideTransitionToast` — tallies `decideTransitionToast` over
 * every customer that WENT THROUGH an auto-staging transition (`stagingIds`),
 * using the before/after `Map`s from `latestStageFailuresForCustomers`. Pure
 * (no supabase, no intl) → testable in isolation from the toast copy.
 *
 * @returns {{ created: number, skipped: number, unconfirmed: number }}
 */
export function decideBulkTransitionToast({ stagingIds, before, after }) {
  let created = 0;
  let skipped = 0;
  let unconfirmed = 0;
  for (const rawId of stagingIds || []) {
    const id = String(rawId);
    const b = before?.get(id) ?? { row: null, errored: false };
    const a = after?.get(id) ?? { row: null, errored: false };
    const decision = decideTransitionToast({ before: b, after: a });
    if (decision.kind === 'created') created += 1;
    else if (decision.kind === 'noInvoice') skipped += 1;
    else unconfirmed += 1;
  }
  return { created, skipped, unconfirmed };
}

/**
 * Pure decision + copy for the v2 RPC's own outcome — no query, no before/
 * after dance, just the truth the RPC already returned in the same call.
 * Exported separately from `reportLifecycleTransitionToast` so it unit-tests
 * without a DOM/supabase mock.
 *
 * @param {{
 *   name: string,
 *   transitionTo: 'active' | 'closed',
 *   outcome: { staged: boolean, invoice_number?: string|number, invoice_number_year?: number,
 *     total?: number|null, left_out?: { count: number, total: number|null }, skip_reason?: string },
 *   t: (id: string, def: string, values?: object) => string,
 *   push: (type: 'success'|'info', message: string) => void,
 * }} params
 */
export function reportOutcomeToast({ name, transitionTo, outcome, t, push }) {
  const leftOver = outcome?.left_out?.count ?? 0;
  const leftOverAmount = typeof outcome?.left_out?.total === 'number' ? ` (${formatCurrency(outcome.left_out.total)})` : '';
  const companions = Number(outcome?.companion_count) || 0;
  const companionSuffix = companions > 0
    ? ` ${t(
      'billing.lifecycleActivate.v2CompanionSuffix',
      'Includes {count, plural, one {# weekly, extra, or off-season visit} other {# weekly, extra, or off-season visits}}.',
      { count: companions },
    )}`
    : '';

  if (!outcome?.staged) {
    if (!outcome?.skip_reason && outcome?.status_changed === false) {
      push('info', t(
        'billing.lifecycleActivate.alreadyChangedToast',
        '{name} was already in that status — check Billing → Collect for its invoice.',
        { name },
      ));
      return;
    }
    const r = REASON_LABEL[outcome?.skip_reason] || REASON_LABEL.unknown_error;
    const reason = t(r.id, r.def);
    push('info', t(
      'billing.lifecycleActivate.noInvoiceToast',
      'No invoice was created for {name} ({reason}). Its visits stay in To collect.',
      { name, reason },
    ));
    return;
  }

  // Shared formatter (INV-YYYY-NNNN, zero-padded) — matches every other
  // invoice-number surface in the app; a bare `INV-${n}` omitted the year and
  // the zero-padding. invoice_number_year is admin|billing-only in the v2
  // outcome payload (money identity), so it can be undefined — the formatter
  // falls back to the current year, same as every other caller.
  const invoiceNumber = outcome.invoice_number != null
    ? formatInvoiceNumber(outcome.invoice_number, outcome.invoice_number_year)
    : '';
  const baseKey = transitionTo === 'active' ? 'billing.lifecycleActivate.v2ActivatedToast' : 'billing.lifecycleActivate.v2ClosedToast';
  const baseDefault = transitionTo === 'active'
    ? '{name} activated — {invoiceNumber} created in Billing → Collect.'
    : '{name} closed — {invoiceNumber} created in Billing → Collect.';
  const baseText = t(baseKey, baseDefault, { name, invoiceNumber });

  if (leftOver === 0) {
    push('success', `${baseText}${companionSuffix}`);
    return;
  }

  push('success', `${baseText} ${t(
    'billing.lifecycleActivate.v2LeftOutSuffix',
    '{count, plural, one {# visit} other {# visits}} left out{amount}.',
    { count: leftOver, amount: leftOverAmount },
  )}${companionSuffix}`);
}

/**
 * Push the honest, role-aware transition toast AFTER a successful status change.
 *
 * @param {{
 *   customerId: string,
 *   name: string,
 *   transitionTo: 'active' | 'closed',
 *   before: { row, errored } | null,   // snapshot captured BEFORE the RPC (canBill only)
 *   intl: { formatMessage: Function },
 *   push: (type: 'success'|'info', message: string) => void,
 *   canBill: boolean,                  // admin/billing → can reach Drafts
 *   tenantId?: string,                 // OPTIONAL — doubly-scopes the AFTER read
 *   outcome?: object|null,             // v2 RPC's own return payload (2026-09-18) —
 *     when present, skip the before/after stage-failure dance entirely and
 *     speak from the RPC's own truth: {staged, invoice_number, total,
 *     left_out:{count,total|null}, skip_reason}. `before` is ignored when
 *     `outcome` is passed.
 * }} params
 */
export async function reportLifecycleTransitionToast({ customerId, name, transitionTo, before, intl, push, canBill, tenantId, outcome }) {
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  // supervisor / worker: can't reach Drafts → generic, no misleading pointer.
  if (!canBill) {
    push('success', transitionTo === 'active'
      ? t('feedback.lifecycle.poolActivated', 'Pool activated.')
      : t('feedback.lifecycle.poolClosed', 'Pool closed.'));
    return;
  }

  // v2 outcome path (2026-09-18) — the RPC already told us the truth in the
  // SAME call; no need for the before/after stage-failure read at all.
  if (outcome) {
    reportOutcomeToast({ name, transitionTo, outcome, t, push });
    return;
  }

  const after = await latestStageFailure(customerId, tenantId);
  const decision = decideTransitionToast({ before, after });

  if (decision.kind === 'unconfirmed') {
    push('info', t(
      'billing.lifecycleActivate.activatedUnconfirmedToast',
      "{name}'s status changed, but we couldn't confirm the invoice — check Billing → Collect.",
      { name },
    ));
    return;
  }

  if (decision.kind === 'noInvoice') {
    const r = REASON_LABEL[decision.reason] || REASON_LABEL.unknown_error;
    const reason = t(r.id, r.def);
    push('info', t(
      'billing.lifecycleActivate.noInvoiceToast',
      'No invoice was created for {name} ({reason}). Its visits stay in To collect.',
      { name, reason },
    ));
    return;
  }

  push('success', transitionTo === 'active'
    ? t('billing.lifecycleActivate.activatedToast', '{name} activated — its opening invoice was created automatically and is waiting in Billing → Collect, ready to review and send.', { name })
    : t('billing.lifecycleActivate.closedToast', '{name} closed — its closing invoice was created automatically and is waiting in Billing → Collect, ready to review and send.', { name }));
}

/**
 * Bulk twin of `reportLifecycleTransitionToast` — ONE honest toast for a bulk
 * move that MAY contain a mix of auto-staging rows (opening→active /
 * closing→closed) and plain rows (no invoice concern). `stagingIds` must be
 * the subset of the moved ids whose FROM→TO pair is an auto-staging
 * transition (`isAutoStagingTransition`) — everything else is invisible to
 * this function's invoice accounting.
 *
 * @param {{
 *   stagingIds: string[],               // subset of moved ids that auto-stage
 *   movedCount: number,                 // total customers moved (all ids)
 *   transitionTo: 'active' | 'closed',
 *   before: Map<string,{row,errored}> | null,  // snapshot BEFORE the RPC (canBill only)
 *   intl: { formatMessage: Function },
 *   push: (type: 'success'|'info', message: string) => void,
 *   canBill: boolean,
 *   tenantId?: string,                 // OPTIONAL — doubly-scopes the AFTER read
 * }} params
 */
export async function reportBulkLifecycleTransitionToast({ stagingIds, movedCount, transitionTo, before, intl, push, canBill, tenantId }) {
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);

  // supervisor / worker: can't reach Drafts → generic, no misleading pointer.
  if (!canBill) {
    push('success', transitionTo === 'active'
      ? t('feedback.lifecycle.bulkPoolsActivated', '{count, plural, one {# pool activated.} other {# pools activated.}}', { count: movedCount })
      : t('feedback.lifecycle.bulkPoolsClosed', '{count, plural, one {# pool closed.} other {# pools closed.}}', { count: movedCount }));
    return;
  }

  const ids = Array.isArray(stagingIds) ? stagingIds : [];
  const after = await latestStageFailuresForCustomers(ids, tenantId);
  const { created, skipped, unconfirmed } = decideBulkTransitionToast({ stagingIds: ids, before, after });

  if (unconfirmed > 0) {
    push('info', t(
      'billing.lifecycleActivate.bulkUnconfirmedToast',
      "{count, plural, one {# status change saved} other {# status changes saved}}, but we couldn't confirm {n, plural, one {# invoice} other {# invoices}} — check Billing → Collect.",
      { count: movedCount, n: unconfirmed },
    ));
    return;
  }

  if (created > 0 && skipped === 0) {
    push('success', t(
      'billing.lifecycleActivate.bulkCreatedToast',
      '{count, plural, one {# draft created} other {# drafts created}} — review them in Billing → Collect.',
      { count: created },
    ));
    return;
  }

  if (created > 0 && skipped > 0) {
    push('success', t(
      'billing.lifecycleActivate.bulkPartialToast',
      '{created, plural, one {# draft created} other {# drafts created}} · {skipped} without an invoice (their visits stay in To collect).',
      { created, skipped },
    ));
    return;
  }

  if (ids.length > 0 && created === 0) {
    push('info', t(
      'billing.lifecycleActivate.bulkNoneToast',
      '{count, plural, one {No invoice was created (# skipped)} other {No invoices were created (# skipped)}} — their visits stay in To collect.',
      { count: skipped },
    ));
    return;
  }

  // No staging rows in this batch at all — a plain bulk move, no invoice story.
  push('success', t(
    'billing.lifecycleActivate.bulkUpdatedToast',
    '{count, plural, one {# customer updated.} other {# customers updated.}}',
    { count: movedCount },
  ));
}
